import { useQuery } from '@tanstack/react-query';
import { useLocalSearchParams } from 'expo-router';
import { useEffect, useMemo } from 'react';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { twJoin } from 'tailwind-merge';

import { Header } from '@/components/header';
import { getScreenColumnClasses } from '@/components/screenColumn';
import { Button, ButtonText } from '@/components/ui/button';
import { Heading } from '@/components/ui/heading';
import { ScrollView } from '@/components/ui/scroll-view';
import { Spinner } from '@/components/ui/spinner';
import { Text } from '@/components/ui/text';
import { VStack } from '@/components/ui/vstack';
import { ROUTES } from '@/constants/routes';
import {
  SurveyResults,
  usePollHistory,
  getSurveyResultsQueryOptions,
  isSurveyResultsNotFoundError,
} from '@/features/polls';
import { getAnsweredQuestionNames } from '@/features/polls/model';
import { useColors } from '@/hooks/useColors';
import { Sentry } from '@/util/sentry';

const SCREEN_COLUMN = getScreenColumnClasses({ maxWidth: 'max-w-screen-md' });

/**
 * One poll's results, with the user's own answers marked when they gave any.
 *
 * Asks for results by group id rather than loading the survey, because a closed
 * or revised poll has no published version left to load. Anyone signed in may
 * read a poll's results, so the response history only supplies the answers to
 * mark, and a history that fails or lacks the poll just leaves them unmarked.
 * When the API answers 404 the group is not a poll, which is expected and not
 * reported.
 */
export default function PollResultsScreen() {
  const { groupId } = useLocalSearchParams<{ groupId: string }>();

  const { bottom } = useSafeAreaInsets();

  const { colors } = useColors();

  const { polls } = usePollHistory();

  const poll = polls.find((answeredPoll) => answeredPoll.surveyGroupId === groupId);

  const resultsQueryOptions = getSurveyResultsQueryOptions(groupId);

  const {
    data: results,
    isLoading: isResultsLoading,
    isError: isResultsError,
    error: resultsError,
    refetch: refetchResults,
  } = useQuery(resultsQueryOptions);

  const isResultsMissing = isResultsError && isSurveyResultsNotFoundError(resultsError);

  useEffect(() => {
    if (!isResultsError || isSurveyResultsNotFoundError(resultsError)) {
      return;
    }

    Sentry.captureException(resultsError, {
      tags: { component: 'PollResultsScreen', source: 'poll-results' },
      extra: {
        surveyGroupId: groupId,
        errorMessage: resultsError instanceof Error ? resultsError.message : String(resultsError),
      },
    });
  }, [isResultsError, resultsError, groupId]);

  const questionNames = useMemo(
    () =>
      results && poll
        ? getAnsweredQuestionNames(
            results.questions.map((question) => question.name),
            poll.answers
          )
        : undefined,
    [results, poll]
  );

  let content: React.ReactNode;

  if (isResultsLoading) {
    content = <Spinner size="large" className="py-10" />;
  } else if (isResultsError && !isResultsMissing) {
    content = (
      <VStack className="items-center gap-4 py-10">
        <Text className="text-center text-typography-700">We couldn't load these results.</Text>

        <Button variant="outline" action="secondary" onPress={() => refetchResults()}>
          <ButtonText>Try again</ButtonText>
        </Button>
      </VStack>
    );
  } else if (results) {
    content = (
      <SurveyResults results={results} answers={poll?.answers} questionNames={questionNames} />
    );
  } else {
    content = (
      <Text className="py-10 text-center text-typography-700">
        Results aren't available for this poll.
      </Text>
    );
  }

  return (
    <VStack className={twJoin('flex-1', SCREEN_COLUMN.frame)} style={{ paddingBottom: bottom }}>
      <Header
        contentScrollsUnderHeader
        statusBarBackgroundColor={colors.background[0]}
        title="Poll results"
        isBackButton
        backRoute={ROUTES.ACCOUNT_POLLS}
      />

      <ScrollView
        contentInsetAdjustmentBehavior="automatic"
        className="w-full flex-1"
        contentContainerClassName={twJoin('gap-6 py-6', SCREEN_COLUMN.content)}>
        {poll || results ? <Heading size="h3-lg">{poll?.name ?? results?.name}</Heading> : null}

        {content}
      </ScrollView>
    </VStack>
  );
}
